import { correrDia, nombreDelPeriodo, rango as rangoTexto } from '@/modules/tablero/periodo/cifras'
import { hoy } from '@/modules/tablero/semana'
import { fecha } from '@/shared/utilidades/formato'

import { bordes } from './periodo'
import estilos from './PaginaCartera.module.css'

/**
 * LA CONSULTA: UN DÍA, UNA SEMANA, UN MES, UN AÑO O EL RANGO QUE SE QUIERA.
 *
 * Johan: «debería poder ordenarlo por semanas», y después: «que la pueda
 * consultar por rango de fechas o día en específico». Arriba van los atajos
 * —Hoy, Esta semana, Este mes, Este año— y dos más para lo que no es
 * redondo: «Un día» (con su fecha) y «Rango» (desde, hasta). Los periodos
 * redondos se recorren con ‹ ›. La semana es de lunes a domingo, como en
 * el tablero.
 */
export type Modo = 'dia' | 'semana' | 'mes' | 'año' | 'rango'
export type Consulta = { modo: Modo; desde: string; hasta: string }

/** La consulta que contiene un día, en el modo dado (salvo «rango»). */
export function consultaDe(modo: Exclude<Modo, 'rango'>, dia: string): Consulta {
  if (modo === 'dia') return { modo, desde: dia, hasta: dia }
  return { modo, ...bordes(modo, dia) }
}

/** Cómo se nombra lo consultado: «jue 1 oct», «28 sept – 4 oct», «octubre de 2026»… */
export function nombreDeConsulta(c: Consulta): string {
  if (c.modo === 'dia') return fecha(c.desde)
  if (c.modo === 'rango') return rangoTexto(c.desde, c.hasta)
  return nombreDelPeriodo(c.modo, c.desde, c.hasta)
}

const ATAJOS: [Exclude<Modo, 'rango'>, string][] = [
  ['dia', 'Hoy'],
  ['semana', 'Esta semana'],
  ['mes', 'Este mes'],
  ['año', 'Este año'],
]

export function SelectorDePeriodo({
  consulta: c,
  onConsulta,
}: {
  consulta: Consulta
  onConsulta: (c: Consulta) => void
}) {
  const hoyDia = hoy()
  const enCurso = c.desde <= hoyDia && hoyDia <= c.hasta
  const esAtajo = (m: Exclude<Modo, 'rango'>) =>
    c.modo === m && c.desde === consultaDe(m, hoyDia).desde
  const unDia = c.modo === 'dia' && c.desde !== hoyDia
  const redondo = c.modo !== 'rango'

  return (
    <div className={estilos.periodo}>
      <div className={estilos.filtros} role="group" aria-label="Qué periodo consultar">
        {ATAJOS.map(([modo, texto]) => (
          <button
            key={modo}
            type="button"
            className={esAtajo(modo) ? estilos.filtroVivo : undefined}
            aria-pressed={esAtajo(modo)}
            onClick={() => onConsulta(consultaDe(modo, hoyDia))}
          >
            {texto}
          </button>
        ))}
        <button
          type="button"
          className={unDia ? estilos.filtroVivo : undefined}
          aria-pressed={unDia}
          onClick={() => onConsulta(consultaDe('dia', correrDia(hoyDia, -1)))}
        >
          Un día
        </button>
        <button
          type="button"
          className={c.modo === 'rango' ? estilos.filtroVivo : undefined}
          aria-pressed={c.modo === 'rango'}
          onClick={() =>
            onConsulta({ modo: 'rango', desde: bordes('mes', hoyDia).desde, hasta: hoyDia })
          }
        >
          Rango
        </button>
      </div>

      {/* Un día: su fecha. Rango: desde y hasta. Los demás se recorren. */}
      {c.modo === 'dia' && (
        <label className={estilos.fechaCampo}>
          <span>Día</span>
          <input
            type="date"
            value={c.desde}
            max={hoyDia}
            onChange={(e) => e.target.value && onConsulta(consultaDe('dia', e.target.value))}
          />
        </label>
      )}
      {c.modo === 'rango' && (
        <div className={estilos.rangoCampos}>
          <label className={estilos.fechaCampo}>
            <span>Desde</span>
            <input
              type="date"
              value={c.desde}
              max={c.hasta}
              onChange={(e) => e.target.value && onConsulta({ ...c, desde: e.target.value })}
            />
          </label>
          <label className={estilos.fechaCampo}>
            <span>Hasta</span>
            <input
              type="date"
              value={c.hasta}
              min={c.desde}
              onChange={(e) => e.target.value && onConsulta({ ...c, hasta: e.target.value })}
            />
          </label>
        </div>
      )}

      {redondo && (
        <div className={estilos.navegador} role="group" aria-label="Recorrer">
          <button
            type="button"
            className={estilos.flecha}
            onClick={() =>
              onConsulta(consultaDe(c.modo as Exclude<Modo, 'rango'>, correrDia(c.desde, -1)))
            }
            aria-label="Anterior"
          >
            ‹
          </button>
          <span className={estilos.rango}>
            {nombreDeConsulta(c)}
            {enCurso && <span className={estilos.enCurso}>en curso</span>}
          </span>
          <button
            type="button"
            className={estilos.flecha}
            onClick={() =>
              onConsulta(consultaDe(c.modo as Exclude<Modo, 'rango'>, correrDia(c.hasta, 1)))
            }
            disabled={enCurso}
            aria-label="Siguiente"
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}
